/* ============================================================
   RUIN.OS — DATA SEAM  (window.RuinOS)
   ------------------------------------------------------------
   This is the ONE file your backend dev edits to go live.
   Every page (Account, Market, Game) reads its data from here
   and from nowhere else. The markup never calls fetch directly.

   HOW IT WORKS NOW (no backend):
     RuinOS.apiBase is "" → every method returns the MOCK_* data
     below, so the pages look alive in the theme editor and on a
     live store before the backend exists.

   HOW TO GO LIVE (backend dev, later):
     1. Set RuinOS.apiBase to the deployed backend, e.g.
          window.RuinOS.apiBase = "https://api.ruin.os";
        (or inject it from a theme setting / <meta> — see README).
     2. Set RuinOS.userId to the logged-in player's stable id.
        On Shopify you can use the customer id when logged in:
          {{ customer.id }}  → passed in via the section markup.
     3. That's it. The fetch() branches below activate automatically
        and the mock data is ignored. The request/response shapes
        already match SHOPIFY_HANDOFF.md §5 exactly.

   CONTRACT (from the handoff — do not drift from these):
     GET  {apiBase}/me?userId=...      -> see MOCK_ME
     GET  {apiBase}/market             -> see MOCK_MARKET  (spell pool + community)
     POST {apiBase}/run                -> { ok: true }
   ============================================================ */
(function () {
  "use strict";

  var RuinOS = {
    // ---- CONFIG (dev sets these to go live) --------------------------------
    // LIVE: points at the Cloudflare Worker ("the brain"). It's the only thing
    // that talks to the database. Only /me and /run are live so far; /market and
    // /profile still use mock data below until those endpoints are built.
    apiBase: "https://ruinos-api.uuhdoubled.workers.dev",
    userId: null,       // Shopify customer id — set per page from {{ customer.id }}

    // ---- internal ----------------------------------------------------------
    // "Live" needs BOTH a backend AND a known player. Logged-out visitors and the
    // theme editor have no userId, so they safely fall back to the demo data.
    _live: function () { return !!this.apiBase && !!this.userId; },
    _url: function (path) { return this.apiBase.replace(/\/$/, "") + path; },

    /* GET /me — the signed-in player's state (Account page).
       Returns: { shareHandle, avatar, email, phone,
                  bestRound, bestScore,  // personal records
                  gamesBeaten,           // lifetime times they beat the game
                  spells: [{place,seed}],// their unspent spells. place = CURRENT spot in
                                         //   the storewide FIFO line (mint position minus
                                         //   sales made since; backend recomputes), seed =
                                         //   the spell's unique share id
                  commissionCents,       // lifetime money earned (green on the page)
                  payout: { connected,   // Stripe bank link status
                            bankLast4,   // "4242" once connected (display only)
                            balanceCents // earned but not yet auto-transferred
                          },
                  communityDiscount:{activePct, contributorsThisMonth} }
       Share link is per-spell: /pages/market?spell=<seed> — sharing a specific
       spell consumes THAT spell when the order lands (exact attribution).
       NOTE: personal discount % is DERIVED from bestRound: 3% per round,
       capped at 30% (rounds 1-9 = campaign; round 10 = surviving Endless wave 1,
       which is how the full 30% is reached). The backend MUST apply the SAME rule at
       checkout, or the number shown won't match what's charged.                 */
    me: function () {
      if (!this._live()) return Promise.resolve(clone(MOCK_ME));
      var q = "?customerId=" + encodeURIComponent(this.userId);
      return fetch(this._url("/me" + q))
        .then(function (r) { return r.json(); })
        .then(function (d) { return apiMeToView(d); })
        .catch(function () { return clone(MOCK_ME); });   // network hiccup → show demo, never a blank page
    },

    /* Save profile edits from the Manage Account modal (avatar/email/phone).
       No-op in mock mode; POST /profile once the backend exists.               */
    updateProfile: function (patch) {
      // TODO: wire to Worker POST /profile once that endpoint exists. Mock for now.
      return Promise.resolve({ ok: true, mock: true });
    },

    /* Change the player's display name. Resolves to { ok, handle } on success, or
       { ok:false, error, message } if it's taken / on cooldown / invalid.        */
    rename: function (handle) {
      if (!this._live()) return Promise.resolve({ ok: false, error: "offline", message: "Log in to change your name." });
      return fetch(this._url("/rename"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: this.userId, handle: handle })
      }).then(function (r) { return r.json(); });
    },

    /* GET /market — the live spell pool + monthly leaderboard (Market page).
       Returns: {
         poolTotal,             // total spells in the line right now
         activeContributors,    // DISTINCT players who still hold >=1 spell in the pool
                                //   (NOT lifetime contributors) — drives "powered by N".
                                //   100 players may have contributed, but if only 4 still
                                //   have unspent spells, this is 4.
         pool:[{ place, handle, avatar }], // the FRONT of the line, ordered — place 1 is next
         leaderboard:[{ name, avatar, gold, round, you? }]  // monthly, gold = one-run score
       }
       The store discount is live while poolTotal > 0 — pool empties, discount off. */
    market: function () {
      // Public page — go live whenever a backend is set (no login needed).
      // customerId (if logged in) just flags "you" on the leaderboard.
      if (!this.apiBase) return Promise.resolve(clone(MOCK_MARKET));
      var q = this.userId ? "?customerId=" + encodeURIComponent(this.userId) : "";
      return fetch(this._url("/market" + q))
        .then(function (r) { return r.json(); })
        .then(function (d) { return apiMarketToView(d); })
        .catch(function () { return clone(MOCK_MARKET); });
    },

    /* Ensure a customer-locked Shopify discount code exists at the player's
       current %, and return { ok, pct, code }. Mock mode returns no code.       */
    myDiscount: function () {
      if (!this._live()) return Promise.resolve({ ok: false, pct: 0, code: null });
      return fetch(this._url("/my-discount?customerId=" + encodeURIComponent(this.userId)))
        .then(function (r) { return r.json(); })
        .catch(function () { return { ok: false, pct: 0, code: null }; });
    },

    /* ---- Stripe payouts ----------------------------------------------------
       payoutStatus -> { connected, payoutsEnabled, pendingCents, paidCents,
                         commissionCents }. onboard -> { url } to redirect to.
       cashout -> { ok, paidCents } or { ok:false, message }.                  */
    payoutStatus: function () {
      var empty = { connected: false, payoutsEnabled: false, pendingCents: 0, paidCents: 0, commissionCents: 0 };
      if (!this._live()) return Promise.resolve(empty);
      return fetch(this._url("/payout/status?customerId=" + encodeURIComponent(this.userId)))
        .then(function (r) { return r.json(); }).catch(function () { return empty; });
    },
    payoutOnboard: function (returnUrl, terms) {
      if (!this._live()) return Promise.resolve({ ok: false });
      var q = "?customerId=" + encodeURIComponent(this.userId) + (returnUrl ? "&return=" + encodeURIComponent(returnUrl) : "") + (terms ? "&terms=" + encodeURIComponent(terms) : "");
      return fetch(this._url("/payout/onboard" + q))
        .then(function (r) { return r.json(); }).catch(function () { return { ok: false }; });
    },
    payoutCashout: function () {
      if (!this._live()) return Promise.resolve({ ok: false, message: "Log in first." });
      return fetch(this._url("/payout/cashout"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: this.userId })
      }).then(function (r) { return r.json(); }).catch(function () { return { ok: false, message: "Network error." }; });
    },
    payoutDashboard: function () {
      if (!this._live()) return Promise.resolve({ ok: false });
      return fetch(this._url("/payout/dashboard?customerId=" + encodeURIComponent(this.userId)))
        .then(function (r) { return r.json(); }).catch(function () { return { ok: false }; });
    },

    /* Pixel avatar: a 1024-char string (32x32, row-major), each 0=black 1=white
       2=green. loadAvatar returns the saved string (or null); saveAvatar stores it. */
    loadAvatar: function () {
      if (!this._live()) return Promise.resolve({ ok: false, pixels: null });
      return fetch(this._url("/avatar?customerId=" + encodeURIComponent(this.userId)))
        .then(function (r) { return r.json(); })
        .catch(function () { return { ok: false, pixels: null }; });
    },
    saveAvatar: function (slots, active) {
      if (!this._live()) return Promise.resolve({ ok: false, message: "Log in to save." });
      return fetch(this._url("/avatar"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: this.userId, slots: slots, active: active })
      }).then(function (r) { return r.json(); });
    },

    /* Turn a 1024-char pixel string (32x32; 0=black 1=white 2=green) into a
       crisp 32x32 PNG data URL. Cached so repeats are free. Returns null if
       there's no valid avatar. Use in an <img> with image-rendering:pixelated. */
    avatarURL: function (pixels) {
      if (!pixels || pixels.length !== 1024) return null;
      if (!this._avc) this._avc = {};
      if (this._avc[pixels]) return this._avc[pixels];
      var pal = ['#000000', '#ffffff', '#3bec74'];
      var cn = document.createElement('canvas'); cn.width = 32; cn.height = 32;
      var cx = cn.getContext('2d');
      for (var r = 0; r < 32; r++) for (var c = 0; c < 32; c++) { cx.fillStyle = pal[+pixels[r * 32 + c] || 0]; cx.fillRect(c, r, 1, 1); }
      var url = cn.toDataURL();
      this._avc[pixels] = url;
      return url;
    },

    /* Cast a spell = write the contributor id onto the cart as a hidden cart
       attribute (`_spell_contributor`). This part is REAL even without the
       backend — it's just a Shopify cart write. The discount itself is applied
       server-side by the Shopify Function that reads this attribute (handoff §4).
       `handle` is the spell/contributor handle. Resolves to the updated cart.  */
    castSpell: function (handle) {
      return fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { _spell_contributor: handle } })
      }).then(function (r) { return r.json(); });
    },

    /* POST /run — report a finished run (the game calls this; here for parity
       with the handoff so the dev has one client). No-op in mock mode.        */
    submitRun: function (run) { return this._report("/run", run); },   // progress (each round)
    submitBeat: function (run) { return this._report("/beat", run); }, // one-time (final boss, round 9)

    _report: function (path, run) {
      if (!this._live()) return Promise.resolve({ ok: true, mock: true });
      var body = {
        customerId: run.customerId || this.userId,
        round: (run.round != null) ? run.round : run.maxLevel,
        score: run.score
      };
      return fetch(this._url(path), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      }).then(function (r) { return r.json(); });
    },

    /* ---- Guest progress: "play now, save on sign-up" ----------------------
       Guests aren't logged in, so their runs can't be attributed to anyone.
       We stash their BEST run in this browser (localStorage). When they later
       sign in from the "save your run" prompt, claimGuestRun() replays it under
       their account via the normal /run (or /beat) endpoint — the backend keeps
       the best, so it merges in. Best-effort by design: same browser only, and
       no worse than the existing (client-trusted) run reporting.               */
    _lsGet: function (k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    _lsSet: function (k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
    _lsDel: function (k) { try { localStorage.removeItem(k); } catch (e) {} },

    guestRun: function () {
      var raw = this._lsGet("ruin_guest_run"); if (!raw) return null;
      try { var g = JSON.parse(raw); return { round: Number(g.round) || 0, score: Number(g.score) || 0 }; }
      catch (e) { return null; }
    },
    noteGuestRun: function (round, score) {
      var g = this.guestRun() || { round: 0, score: 0 };
      var best = { round: Math.max(g.round, Number(round) || 0), score: Math.max(g.score, Number(score) || 0) };
      this._lsSet("ruin_guest_run", JSON.stringify(best));
      return best;
    },
    markClaimPending: function () { this._lsSet("ruin_claim", "1"); },

    /* After sign-in: if the player asked to save a guest run, replay it to their
       new account. Resolves { claimed, round, pct }. Safe to call on any load;
       does nothing unless there's a pending claim + a logged-in userId.         */
    claimGuestRun: function () {
      var self = this;
      if (!this.userId || this._lsGet("ruin_claim") !== "1") return Promise.resolve({ claimed: false });
      var g = this.guestRun();
      var done = function () { self._lsDel("ruin_claim"); self._lsDel("ruin_guest_run"); };
      if (!g || g.round < 1) { done(); return Promise.resolve({ claimed: false }); }
      // /claim transfers the earned DISCOUNT only (best_round, capped 30). Spells require a
      // real logged-in run — a guest run is client-reported and can't be trusted to mint one.
      return fetch(this._url("/claim"), {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId: this.userId, round: g.round })
      }).then(function (r) { return r.json(); }).then(function () {
        done();
        try { sessionStorage.removeItem("ruin_disc"); } catch (e) {}   // let the auto-apply re-run with the new %
        self.myDiscount();                                             // mint/update their locked code at the new %
        return { claimed: true, round: g.round, pct: Math.max(0, Math.min(30, g.round * 3)) };
      }).catch(function () { return { claimed: false }; });   // keep it for a retry next login
    }
  };

  /* Map the Worker's /me response to the exact shape the Account page reads.
     Fills in fields the backend doesn't send yet (payout, communityDiscount)
     with safe placeholders so the page never crashes on a missing field. */
  // backend stores an avatar keyword; the pages render a glyph.
  function avatarGlyph(a) { var m = { skull: "☠" }; return m[a] || a || "☠"; }

  // Map the Worker's /market response to the shape the Market page reads,
  // turning avatar keywords into glyphs.
  function apiMarketToView(d) {
    d = d || {};
    return {
      poolTotal: d.poolTotal || 0,
      activeContributors: d.activeContributors || 0,
      pool: (d.pool || []).map(function (x) { return { place: x.place, handle: x.handle, avatar: avatarGlyph(x.avatar), avatarPx: x.avatarPx || null }; }),
      leaderboard: (d.leaderboard || []).map(function (x) {
        return { name: x.name, avatar: avatarGlyph(x.avatar), avatarPx: x.avatarPx || null, gold: x.gold, round: x.round, you: x.you };
      }),
    };
  }

  function apiMeToView(d) {
    d = d || {};
    var cents = d.commissionCents || 0;
    var avatarMap = { skull: "☠" };   // backend stores a keyword; page renders a glyph
    return {
      shareHandle: d.shareHandle || "PLAYER",
      avatar: avatarMap[d.avatar] || d.avatar || "☠",
      email: d.email || null,
      phone: d.phone || null,
      bestRound: d.bestRound || 0,
      bestScore: d.bestScore || 0,
      gamesBeaten: d.gamesBeaten || 0,
      commissionCents: cents,
      spells: Array.isArray(d.spells) ? d.spells : [],
      payout: { connected: false, bankLast4: null, balanceCents: cents },
      communityDiscount: { activePct: 20, contributorsThisMonth: 0 }
    };
  }

  // ---- MOCK DATA (mirrors the handoff; remove or ignore once live) ----------
  var MOCK_ME = {
    shareHandle: "ASHEN_KING",
    avatar: "☠",               // ☠ — one of the preset glyphs (backend stores the choice)
    email: "uuhdoubled@gmail.com",
    phone: "(562) 555-1234",
    bestRound: 26,                  // furthest round reached → 30% off (3%/round, capped)
    bestScore: 48210,               // personal best (total gold in a run)
    gamesBeaten: 7,                 // beat it 7 times: one spell used (paid out),
    commissionCents: 3800,          //   $38.00 earned from that sale
    spells: [                       // six spells waiting, current places in line
      { place: 30,  seed: "7f3a91" },
      { place: 55,  seed: "2c8ee0" },
      { place: 78,  seed: "9b1d4a" },
      { place: 102, seed: "04f7c3" },
      { place: 133, seed: "e35b12" },
      { place: 161, seed: "aa90d7" }
    ],
    payout: { connected: false, bankLast4: null, balanceCents: 3800 },
    communityDiscount: { activePct: 20, contributorsThisMonth: 13 }
  };

  var MOCK_MARKET = {
    poolTotal: 161,                 // whole line; the strip shows the front of it
    activeContributors: 9,          // distinct players who still have spells in the pool
    pool: [
      { place: 1,  handle: "GRAVELORD",   avatar: "♜" },
      { place: 2,  handle: "HEX_MOTHER",  avatar: "☾" },
      { place: 3,  handle: "RUST_SAINT",  avatar: "⚔" },
      { place: 4,  handle: "PALE_DROVER", avatar: "♆" },
      { place: 5,  handle: "GRAVELORD",   avatar: "♜" },
      { place: 6,  handle: "MOTH_KING",   avatar: "✶" },
      { place: 7,  handle: "HEX_MOTHER",  avatar: "☾" },
      { place: 8,  handle: "DIM_VICAR",   avatar: "⚑" },
      { place: 9,  handle: "RUST_SAINT",  avatar: "⚔" },
      { place: 10, handle: "OX_BLOOD",    avatar: "⟡" },
      { place: 11, handle: "MOTH_KING",   avatar: "✶" },
      { place: 12, handle: "GRAVELORD",   avatar: "♜" }
    ],
    leaderboard: [
      { name: "GRAVELORD",   avatar: "♜", gold: 91200, round: 27 },
      { name: "HEX_MOTHER",  avatar: "☾", gold: 88040, round: 26 },
      { name: "RUST_SAINT",  avatar: "⚔", gold: 71550, round: 25 },
      { name: "PALE_DROVER", avatar: "♆", gold: 60310, round: 22 },
      { name: "ASHEN_KING",  avatar: "☠", gold: 48210, round: 26, you: true },
      { name: "MOTH_KING",   avatar: "✶", gold: 44870, round: 21 },
      { name: "DIM_VICAR",   avatar: "⚑", gold: 39400, round: 19 },
      { name: "OX_BLOOD",    avatar: "⟡", gold: 31150, round: 17 }
    ]
  };

  function clone(o) { return JSON.parse(JSON.stringify(o)); }

  window.RuinOS = RuinOS;
})();
